import {groupBy, objectFromEntries, partition, setDifference} from '@benchling/js-utils';
import {css, StyleSheet} from 'aphrodite';
import $ from 'jquery';
import React from 'react';
import isEqual from 'react-fast-compare';

import {assertAndContinue} from 'Asserts';
import makeCancelable from 'lib/makeCancelable';
import notifications from 'notifications';
import {GRAY_LIGHT} from 'ui-library/Colors';
import {TreeItem, TreeItemState, TreeItemStateMap} from 'ui-library/select-tree/SelectTreeConstants';
import {sortedRootItems} from 'ui-library/select-tree/SelectTreeUtil';
import Spinner from 'ui-library/Spinner';

import ControlledSelectTree from './ControlledSelectTree';
import {getFilteredSelectTreeItems} from './SelectTreeSearch';

const SCROLL_PADDING = 15; // Pixels from the edge to leave when scrolling item into view
const SCROLL_DURATION = 200; // How long it takes to scroll an item into view

function getDefaultItemState(more: boolean): Omit<TreeItemState, 'baseLeafItems'> {
  return {
    childItems: [],
    isExpanded: false,
    leafItems: [],
    loading: false,
    more,
    offset: 0,
  };
}

export type LoadMorePromise = Promise<
  | {
      items: Array<TreeItem>;
      offset: number;
      more: boolean;
    }
  | undefined
>;

export type SelectTreeProps = RequiredIdsProps & {
  disabled?: boolean;
  /** When enabled, includes an option to deselect all items */
  allowNone: boolean;
  /** When enabled, includes an option to select all items. True by default. */
  includeSelectAllItem?: boolean;
  className?: string;
  items: Array<TreeItem>;
  itemTypeName: string;
  multiple: boolean;
  selectAllText: string;
  selectedIds: Array<string>;
  onChangeSelectedIds: (selectedIds: Array<string>) => void;
  /** When enabled, new items in props.items are expanded */
  shouldExpandNewItems: boolean;
  /** When enabled, newly selected items are expanded */
  shouldExpandOnSelect: boolean;
  /** When enabled, children are not automatically loaded on mount in expanded parents */
  shouldNotLoadMoreOnMount?: boolean;
  submitHandler?: () => void;

  /** An array of leaf items. These are displayed on the top of leaf items until loaded in by loadMore. */
  baseLeafItems: Array<TreeItem>;

  /**
   * Optional: function to call on newly loaded items to partition into expandable and leaf items since they
   * are rendered differently (expandable items are rendered with an arrow to expand/collapse). Default
   * behavior for SelectTree is to treat all newly loaded items as leaves.
   */
  isLoadedItemExpandable?: ((item: TreeItem) => boolean) | null;
  /**
   * Callback for loading in additional items
   * @param {string} parentId The ID of the parentId whose children will be returned
   * @param {number} offset The number of items already fetched
   * @returns An array of select tree leaf items to be loaded into the tree
   */
  loadMore?: (parentId: string, offset: number) => LoadMorePromise;
  query?: string;
  rootItemSortFn: (rootItems: Array<TreeItem>) => Array<TreeItem>;

  /**
   * Optional: function for sorting non-root items. Child items of a given parent are partitioned into
   * expandable and unexpandable (leaf) items. Each of these groups is sorted separately. Expandable items
   * always precede unexpandable items.
   */
  itemSortFn?: (items: Array<TreeItem>) => Array<TreeItem>;

  onLoadMoreRootItems?: () => void;
  moreRootItems?: boolean;
  isLoadingRootItems?: boolean;
  shouldToggleExpandedOnClick: (newClickedItem: string) => boolean;

  /** When absent, all items are selectable. */
  isItemSelectable?: (item: TreeItem) => boolean;

  /** Optional: headers for the table */
  headers?: Array<string> | null;
  /** Optional: column widths for the headers and tree rows */
  columnWidths?: Array<string>;
  isLoading?: boolean;
  setIsLoading?: (loading: boolean) => void;

  itemClassName?: string;
  selectedItemClassName?: string;
  onHoverItem?: (id: string, isHovering: boolean) => void;
};

export type RequiredIdsProps =
  | {
      allowNone?: false;
      /** An optional array of ids for items that cannot be deselected.
       *
       * @todo Currently only implemented for items at the root level.
       *
       * Caller SHOULD ensure that `props.selectedIds` contains all items in `props.requiredRootIds`,
       * and MAY check for their presence in `props.onChangeSelectedIds` as a safety measure.
       *
       * @example
       *  const [selectedIds, setSelectedIds] = useState(requiredIds);
       *  const onChangeSelectedIds = useCallback((newIds) => {
       *     const processedIds = new Set([...newIds, ...requiredIds]);
       *     assertAndContinue(processedIds.size === newIds.length, 'Required IDs were missing - auto-added.');
       *     setSelectedIds([...processedIds]);
       *  }, [setSelectedIds]);
       *  const tree = (
       *    <SelectTree
       *      selectedIds={selectedIds}
       *      onChangeSelectedIds={onChangeSelectedIds}
       *      ...
       *   />
       */
      requiredRootIds: Array<string>;
    }
  | {
      requiredRootIds?: never;
    };

export interface SelectTreeState {
  queryIdsToExpand: Array<string>;
  itemStateMap: TreeItemStateMap;
  clickedItems: Array<string>;
  rootItems: Array<TreeItem>;
}

/**
 * A re-usable component for rendering nested hierarchies (e.g. folders and files) and allowing the user to
 * select one (or more if `multiple`) items in the hierarchy.
 *
 * An expandable item is one that can be the parent of other items in the tree, and can have loadMoreFromIds
 * called on it when it is expanded. Leaf items are items that cannot be expanded in the tree.
 */
export default class SelectTree extends React.Component<SelectTreeProps, SelectTreeState> {
  rootRef = React.createRef<HTMLUListElement>();

  static getTargetScrollTop(root: Element, node: Element): number | null {
    const $root = $(root);
    const $node = $(node);

    // Compute top/bottom of the visible region of the selectTree.
    const top = $root.scrollTop();
    const height = $root.height();
    const bottom = top + height;
    const nodeHeight = $node.height();
    const nodeTop = top + ($node.position().top - $root.position().top);
    const nodeBottom = nodeTop + nodeHeight;

    // Compute top/bottom of the item we want to scroll to.
    let scrollTop;
    if (nodeTop < top) {
      scrollTop = nodeTop - SCROLL_PADDING;
    } else if (nodeBottom > bottom) {
      scrollTop = top + (nodeBottom - bottom) + SCROLL_PADDING;
    } else {
      return null;
    }
    return scrollTop;
  }

  static defaultProps = {
    allowNone: false,
    includeSelectAllItem: true,
    baseLeafItems: [],
    shouldExpandNewItems: true,
    shouldExpandOnSelect: false,
    itemTypeName: 'files',
    selectAllText: 'All Projects',
    rootItemSortFn: sortedRootItems,
    shouldToggleExpandedOnClick: (_newClickedItem: string): false => false,
    itemSortFn: (items: Array<TreeItem>): Array<TreeItem> => items,
  };

  override state: SelectTreeState = {
    ...this.processPropsForState(this.props),
    clickedItems: [],
  };

  private fetchPromises: Array<{
    promise: LoadMorePromise;
    cancel: () => void;
  }> = [];

  override componentDidMount(): void {
    this.fetchPromises = [];

    if (!this.props.shouldNotLoadMoreOnMount) {
      // getInitialState may result in some items expanded initially, so we need to fetch their contents.
      this.loadMoreFromIds(this.props.items.map((item) => item.id));
    }
  }

  override UNSAFE_componentWillReceiveProps(nextProps: SelectTreeProps): void {
    if (
      !isEqual(this.props.items, nextProps.items) ||
      !isEqual(this.props.selectedIds, nextProps.selectedIds) ||
      !isEqual(this.props.baseLeafItems, nextProps.baseLeafItems) ||
      !isEqual(this.props.query, nextProps.query)
    ) {
      this.setState(
        this.processPropsForState(
          nextProps,
          this.state.itemStateMap,
          this.props.selectedIds,
          this.state.queryIdsToExpand
        )
      );
    }
  }

  override componentDidUpdate(prevProps: SelectTreeProps, prevState: SelectTreeState): void {
    // If the user has selected one new item, scroll to view the item.
    if (this.props.selectedIds.length - prevProps.selectedIds.length !== 1) {
      return;
    }
    // On deselect, don't scroll.
    const root = this.rootRef.current!;
    const [newestId] = this.props.selectedIds.slice(-1);
    const node = $(root).find(`[data-node-id='${newestId}']`)[0];
    if (!node) {
      return;
    }
    const scrollTop = SelectTree.getTargetScrollTop(root, node);
    if (scrollTop != null) {
      $(root).stop().animate({scrollTop}, SCROLL_DURATION);
    }
  }

  override componentWillUnmount(): void {
    this.fetchPromises.forEach((cancelablePromise) => {
      cancelablePromise.cancel();
    });
  }

  private canLoadMore(): boolean {
    return Boolean(this.props.loadMore);
  }

  private toggleExpanded = (itemId: string): void => {
    const itemStateMap: TreeItemStateMap = {
      ...this.state.itemStateMap,
      [itemId]: {
        ...this.state.itemStateMap[itemId],
        isExpanded: !this.state.itemStateMap[itemId].isExpanded,
      },
    };
    this.setState({itemStateMap}, () => {
      // Call loadMoreFromIds in state-update callback to ensure that `isExpanded` is true.
      const itemState = this.state.itemStateMap[itemId];
      const hasNotYetLoadedLeaves = this.canLoadMore() && itemState.leafItems.length === 0;
      if (hasNotYetLoadedLeaves) {
        this.loadMoreFromIds([itemId]);
      }
    });
  };

  /**
   * For any id that corresponds to an item that is expanded and has more children to load, load them.
   * Track `loading` boolean state in itemStateMap, as well as the new `leafItems`, `offset`, and `more`.
   */
  private loadMoreFromIds = (ids: Array<string>): void => {
    // Filter ids to those that can loadMore (expanded and has more not-yet-loaded leaves).
    // This is expected to no-op if `props.loadMore` is not specified, since `more` is initialized to false
    // in getDefaultItemState.
    ids = ids.filter((id) => {
      const itemState = this.state.itemStateMap[id];
      return itemState && itemState.isExpanded && itemState.more;
    });
    if (!ids.length) {
      return;
    }
    if (!assertAndContinue(this.canLoadMore(), 'loadMoreFromIds unexpectedly called')) {
      return;
    }
    const itemStateMap = {...this.state.itemStateMap};
    for (const id of ids) {
      itemStateMap[id] = {...itemStateMap[id], loading: true};
    }
    this.setState({itemStateMap});
    ids.forEach((id) => {
      // NB: `Promise` is always asynchronous which avoids an issue with the async nature of setState
      const fetchPromise = makeCancelable(this.props.loadMore!(id, this.state.itemStateMap[id].offset));
      this.fetchPromises.push(fetchPromise);
      fetchPromise.promise
        .then((response) => {
          const {items, offset, more} = response!;
          if (!this.state.itemStateMap.hasOwnProperty(id)) {
            return;
          }
          if (offset !== this.state.itemStateMap[id].offset) {
            // The result is stale if the offset no longer matches.
            return;
          }
          this.setState(this.getStateWithNewLoadedItems(items, offset + items.length, more, id));
        })
        .catch((response) => {
          if (response.isCanceled) {
            return;
          }
          notifications.notifyError(response);
          this.setState({
            itemStateMap: {
              ...this.state.itemStateMap,
              [id]: {
                ...this.state.itemStateMap[id],
                loading: false,
              },
            },
          });
        });
    });
  };

  /**
   * Call `this.props.isLoadedItemExpandable` to decide whether the item should be considered expandable, i.e.
   * it may have children. Returns false if `this.props.isLoadedItemExpandable` is not supplied (default is
   * to treat all newly loaded items as leaves).
   */
  private isLoadedItemExpandable(item: TreeItem): boolean {
    if (this.props.isLoadedItemExpandable) {
      return this.props.isLoadedItemExpandable(item);
    } else {
      return false;
    }
  }

  /**
   * Returns a copy of the state with newly loaded items added to the item state map
   */
  private getStateWithNewLoadedItems(
    items: Array<TreeItem>,
    newOffset: number,
    more: boolean,
    id: string
  ): {itemStateMap: TreeItemStateMap} {
    const newItems = items.filter((item) => !this.state.itemStateMap.hasOwnProperty(item.id));
    const [newExpandableItems, newLeaves] = partition(newItems, (item: TreeItem) =>
      this.isLoadedItemExpandable(item)
    );
    const newItemStateMap: TreeItemStateMap = {...this.state.itemStateMap};
    for (const item of newExpandableItems) {
      newItemStateMap[item.id] = {baseLeafItems: [], ...getDefaultItemState(this.canLoadMore())};
    }

    const newChildItemsUnsorted = [...newItemStateMap[id].childItems, ...newExpandableItems];
    const newLeafItemsUnsorted = [...newItemStateMap[id].leafItems, ...newLeaves];
    const newChildItems = this.props.itemSortFn!(newChildItemsUnsorted);
    const newLeafItems = this.props.itemSortFn!(newLeafItemsUnsorted);

    const rv: {itemStateMap: TreeItemStateMap} = {
      itemStateMap: {
        ...newItemStateMap,
        [id]: {
          ...newItemStateMap[id],
          childItems: newChildItems,
          leafItems: newLeafItems,
          offset: newOffset,
          more,
          loading: false,
        },
      },
    };
    return rv;
  }

  /**
   * Returns a copy of the state with new items added into the state
   */
  private getStateWithNewItemsFromProps(
    newItems: Array<TreeItem>,
    newLeafItems: Array<TreeItem>,
    oldItemStateMap: TreeItemStateMap | null
  ): {itemStateMap: TreeItemStateMap; rootItems: Array<TreeItem>} {
    const ROOT_ID = '_';
    const itemsParentMap = groupBy(newItems, (item) => item.parentId ?? ROOT_ID);
    const leavesParentMap = groupBy(newLeafItems, (item) => item.parentId ?? ROOT_ID);

    const itemStateMap: TreeItemStateMap = {};
    for (const item of newItems) {
      const itemState =
        !oldItemStateMap || !oldItemStateMap.hasOwnProperty(item.id)
          ? getDefaultItemState(this.canLoadMore())
          : oldItemStateMap[item.id];

      const childItems = this.props.itemSortFn!(itemsParentMap[item.id] || []);
      const baseLeafItems = this.props.itemSortFn!(leavesParentMap[item.id] || []);

      // Update children in case those have changed
      itemStateMap[item.id] = {...itemState, childItems, baseLeafItems};
    }

    const rootItems = this.props.rootItemSortFn(itemsParentMap[ROOT_ID] ?? []);
    const rootLeafItems = this.props.rootItemSortFn(leavesParentMap[ROOT_ID] ?? []);

    return {itemStateMap, rootItems: [...rootItems, ...rootLeafItems]};
  }

  /**
   * Returns a list of item ids to expand.
   *
   * An item should be expanded if it's newly selected, is a new normal/leaf item, or is the parent of an item
   * matched by the given query.
   */
  private getItemIdsToExpand(
    props: SelectTreeProps,
    oldItemStateMap: TreeItemStateMap | null,
    oldSelectedValue: Array<string> | null,
    queryDerivedIds: Array<string>
  ): Array<string | null> {
    const newSelectedLeafItemParents = props.shouldExpandOnSelect
      ? props.baseLeafItems.map((item) => item.parentId)
      : [];
    if (oldItemStateMap == null) {
      const initialExpandedItems = props.items
        .filter((item) => item.isInitiallyExpanded)
        .map((item) => item.id);
      return [
        ...newSelectedLeafItemParents,
        ...props.selectedIds,
        ...initialExpandedItems,
        ...queryDerivedIds,
      ];
    }
    const newItems = props.shouldExpandNewItems
      ? props.items.map((item) => item.id).filter((id) => !oldItemStateMap.hasOwnProperty(id))
      : [];
    if (oldSelectedValue == null) {
      return [...newSelectedLeafItemParents, ...newItems, ...queryDerivedIds];
    }
    const newSelectedItems = props.shouldExpandOnSelect
      ? [...setDifference(new Set(props.selectedIds), new Set(oldSelectedValue))]
      : [];

    return [...newItems, ...newSelectedItems, ...newSelectedLeafItemParents, ...queryDerivedIds];
  }

  /**
   * Mutates the itemStateMap to expand or the items listed in itemIdsToExpand
   */
  private setExpandedForItemsInStateMap(
    itemStateMap: TreeItemStateMap,
    items: Array<TreeItem>,
    itemIdsToExpand: Array<string | null>,
    shouldExpand: boolean
  ): void {
    const itemMap = objectFromEntries(items.map((item) => [item.id, item]));
    for (const id of itemIdsToExpand) {
      let currentId: string | null = id;
      while (currentId != null) {
        const itemState = itemStateMap[currentId];
        if (itemState == null) {
          break;
        }
        itemStateMap[currentId] = {
          ...itemState,
          isExpanded: shouldExpand,
        };
        currentId = itemMap[currentId].parentId;
      }
    }
  }

  /**
   * Processes the list of given `items` to derive a list of top-level items and state for each item,
   * tracking children / expanded states.
   * Note that this ideally will be refactored such that we maintain less in state.
   *
   * @param {Object} props - Props passed to this component
   * @param {Object?} oldItemStateMap - The previous mapping as described in the return value of this func.
   *                                    The new mapping inherits, for example, `expanded` state. Optional
   *                                    for first render.
   * @param {Array?} oldSelectedValue - a list of the previously selected items
   * @param {Array?} oldQueryItemsToExpand - a list of the previously expanded items
   * @return {Object} - `rootItems` is the sorted (user/forced folders at top) items that should be rendered.
   *                    `queryIdsToExpand` is the set of items that should be expanded as they are the parents
   *                                       of the items matched by the current query.
   *                    `itemStateMap` is a mapping from item.id to extra derived state:
   *                         - `isExpanded` indicates if the note should be rendered expanded.
   *                         - `childItems` items that should appear as direct descendants
   */
  private processPropsForState(
    props: SelectTreeProps,
    oldItemStateMap: TreeItemStateMap | null = null,
    oldSelectedValue: Array<string> | null = null,
    oldQueryItemsToExpand: Array<string> | null = null
  ): {
    queryIdsToExpand: Array<string>;
    itemStateMap: TreeItemStateMap;
    rootItems: Array<TreeItem>;
  } {
    const {items, baseLeafItems, query} = props;
    const {filteredItems, idsToExpand} = getFilteredSelectTreeItems(items, query);
    const newState = this.getStateWithNewItemsFromProps(filteredItems, baseLeafItems, oldItemStateMap);

    const queryItemsToContract = (oldQueryItemsToExpand || []).filter((item) => !idsToExpand.includes(item));
    this.setExpandedForItemsInStateMap(newState.itemStateMap, filteredItems, queryItemsToContract, false);

    const itemIdsToExpand = this.getItemIdsToExpand(props, oldItemStateMap, oldSelectedValue, idsToExpand);
    this.setExpandedForItemsInStateMap(newState.itemStateMap, filteredItems, itemIdsToExpand, true);

    props.setIsLoading?.(false);
    return {
      queryIdsToExpand: idsToExpand,
      itemStateMap: newState.itemStateMap,
      rootItems: newState.rootItems,
    };
  }

  private handleClickedItemsChange = (newClickedItem: string): void => {
    this.setState({clickedItems: [...this.state.clickedItems, newClickedItem]});
  };

  override render(): JSX.Element {
    return this.props.isLoading ? (
      <div className={css(styles.centered, styles.spinner)}>
        <Spinner />
      </div>
    ) : (
      <ControlledSelectTree
        {...this.props}
        {...this.state}
        canLoadMore={() => this.canLoadMore()}
        handleClickedItemsChange={(newClickedItem) => {
          if (this.props.shouldToggleExpandedOnClick(newClickedItem)) {
            this.toggleExpanded(newClickedItem);
          }
          this.handleClickedItemsChange(newClickedItem);
        }}
        hasQuery={Boolean(this.props.query)}
        loadMoreFromIds={(ids) => {
          this.loadMoreFromIds(ids);
        }}
        onChangeSelectedIds={this.props.onChangeSelectedIds}
        toggleExpanded={(itemId) => {
          this.toggleExpanded(itemId);
        }}
        isLoadingRootItems={Boolean(this.props.isLoadingRootItems)}
        moreRootItems={Boolean(this.props.moreRootItems)}
        onLoadMoreRootItems={this.props.onLoadMoreRootItems}
        itemClassName={this.props.itemClassName}
        selectedItemClassName={this.props.selectedItemClassName}
        onHoverItem={this.props.onHoverItem}
        rootRef={this.rootRef}
      />
    );
  }
}

const styles = StyleSheet.create({
  centered: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    flexDirection: 'column',
    marginBottom: '9px',
  },
  spinner: {
    height: '250px',
    border: `1px solid ${GRAY_LIGHT}`,
  },
});
